import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./lib/auth";
import { Login } from "./pages/Login";
import { Home } from "./pages/Home";
import { Onboarding } from "./pages/Onboarding";
import { PlatformHome } from "./pages/PlatformHome";
import { AutoJoin } from "./pages/AutoJoin";
import { Profile } from "./pages/Profile";
import { Upgrade } from "./pages/Upgrade";
import { ForgotPassword } from "./pages/ForgotPassword";
import { AcceptInvite } from "./pages/AcceptInvite";
import { Signup } from "./pages/Signup";
import { Welcome } from "./pages/Welcome";
import { AmbassadorDashboard } from "./pages/AmbassadorDashboard";
import { TenantAdmin } from "./pages/TenantAdmin";
import { AmbassadorApply } from "./pages/AmbassadorApply";
import { PaymentResult } from "./pages/PaymentResult";
import { Landing } from "./pages/Landing";
import { PreviewCustomer } from "./pages/PreviewCustomer";
import { StaffCashier } from "./pages/StaffCashier";
import { AcceptStaffInvite } from "./pages/AcceptStaffInvite";
import { Impersonate } from "./pages/Impersonate";
import { readPendingJoin } from "./lib/pendingJoin";
import { clearRedirect, readRedirect } from "./lib/redirectAfter";

function Splash() {
  return (
    <div className="grid min-h-dvh place-items-center bg-cream text-leaf-600">
      <div className="font-display text-lg italic">Fideliza</div>
    </div>
  );
}

function RootGate() {
  const { user, claims, loading } = useAuth();
  const location = useLocation();

  if (loading) return <Splash />;

  // Si vienen con ?join=... o tienen un pendingJoin guardado y aún no son miembros,
  // los mandamos al AutoJoin (que crea anonymous + joinea sin tap).
  const queryJoin = new URLSearchParams(location.search).get("join");
  const pendingJoin = readPendingJoin();
  const wantsToJoin = queryJoin ?? pendingJoin;
  if (wantsToJoin && claims.tenantId !== wantsToJoin) {
    return <Navigate to={`/j?join=${encodeURIComponent(wantsToJoin)}`} replace />;
  }

  if (!user) {
    const redirect = readRedirect();
    if (redirect) return <Navigate to={redirect} replace />;
    return <Landing />;
  }

  if (claims.role === "platform_admin") return <Navigate to="/platform" replace />;
  const initialEmail = import.meta.env.VITE_INITIAL_PLATFORM_ADMIN_EMAIL?.toLowerCase();
  if (initialEmail && user.email?.toLowerCase() === initialEmail)
    return <Navigate to="/platform" replace />;

  // Post-login redirect a destino original (si lo había).
  const redirect = readRedirect();
  if (redirect && redirect !== location.pathname) {
    clearRedirect();
    return <Navigate to={redirect} replace />;
  }

  if (!claims.tenantId) return <Navigate to="/onboarding" replace />;
  if (claims.role === "tenant_admin") return <Navigate to="/admin" replace />;
  if (claims.role === "staff") return <Navigate to="/staff/cobro" replace />;
  return <Home />;
}

function PlatformGate() {
  const { user, loading, claims } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  const initialEmail = import.meta.env.VITE_INITIAL_PLATFORM_ADMIN_EMAIL?.toLowerCase();
  if (claims.role === "platform_admin") return <PlatformHome />;
  if (initialEmail && user.email?.toLowerCase() === initialEmail) return <PlatformHome />;
  return <Navigate to="/" replace />;
}

function OnboardingGate() {
  const { user, claims, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (claims.tenantId) return <Navigate to="/" replace />;
  return <Onboarding />;
}

function ProtectedRoute({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/olvide-contrasena" element={<ForgotPassword />} />
      <Route path="/aceptar-invitacion" element={<AcceptInvite />} />
      <Route path="/pago/exito" element={<PaymentResult kind="success" />} />
      <Route path="/pago/error" element={<PaymentResult kind="failure" />} />
      <Route path="/pago/pendiente" element={<PaymentResult kind="pending" />} />
      <Route path="/registrate" element={<Signup />} />
      <Route path="/bienvenido" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />
      <Route path="/j" element={<AutoJoin />} />
      <Route path="/onboarding" element={<OnboardingGate />} />
      <Route path="/platform" element={<PlatformGate />} />
      <Route path="/perfil" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/upgrade" element={<ProtectedRoute><Upgrade /></ProtectedRoute>} />
      <Route path="/embajador" element={<ProtectedRoute><AmbassadorDashboard /></ProtectedRoute>} />
      <Route path="/postular-embajador" element={<ProtectedRoute><AmbassadorApply /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><TenantAdmin /></ProtectedRoute>} />
      <Route path="/preview-cliente" element={<ProtectedRoute><PreviewCustomer /></ProtectedRoute>} />
      <Route path="/staff/cobro" element={<ProtectedRoute><StaffCashier /></ProtectedRoute>} />
      <Route path="/staff/aceptar" element={<AcceptStaffInvite />} />
      <Route path="/impersonate" element={<Impersonate />} />
      <Route path="/" element={<RootGate />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
